/**
 * prepareShareForFamily — build a family-shared EncryptedVaultItem from a
 * personal plaintext VaultItem.
 *
 * Protocol mirrors `shared/src/usageEventCrypto.ts` (X25519 ECIES with HKDF
 * domain separation), but uses a distinct HKDF info label ("share-item-v1")
 * so a leak of one wrap doesn't compromise the other.
 *
 * Steps:
 *   1. Generate a fresh ephemeral X25519 keypair.
 *   2. ECDH(ephPriv, familyPub) → 32-byte shared secret.
 *   3. HKDF(shared, salt = ephPub || familyPub, info = "share-item-v1") → AES-256 key.
 *   4. AES-GCM(JSON.stringify(plaintextContent)) → ciphertext + iv.
 *   5. Return an EncryptedVaultItem with orgId set and ephemeralPublicKey
 *      populated. The shared copy always gets a fresh UUID, independent of
 *      `plainItem.id` — the shared ORG# copy must never collide with the
 *      personal USER# item's id in the client's in-memory `items` list. A
 *      caller may pass `opts.itemId` to pin the shared item's id instead
 *      (used by the document editor so attachment S3 keys can be built
 *      before the item is saved).
 *
 * The `personalEncKey` parameter is part of the signature for future-proofing
 * (callers that hold only the encrypted personal item would need it to recover
 * plaintext first). The current call site in SafeContext already has the
 * decrypted VaultItem in memory, so the parameter is accepted but unused.
 */

import type { EncryptedVaultItem, VaultItem } from '@heybesafe/shared';
import { v4 as uuidv4 } from 'uuid';
import { encryptSharedItem } from '@heybesafe/shared';
// decryptSharedItem moved to @heybesafe/shared so the browser extension can use
// it. Re-exported here for existing web call sites.
export { decryptSharedItem } from '@heybesafe/shared';

export async function prepareShareForFamily(
  plainItem: VaultItem,
  familyPublicKey: Uint8Array,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _personalEncKey: CryptoKey | null,
  orgId: string,
  opts?: { itemId?: string },
): Promise<EncryptedVaultItem & { orgId: string }> {
  // Initial share semantics: re-stamped timestamps so the shared copy's
  // audit trail is independent of the personal item. The id is always a
  // fresh UUID unless the caller opts in via `opts.itemId` (e.g. an id fixed
  // up front so an attachment can be uploaded under it before the item is
  // shared). The ECIES wrap itself is shared with the edit path via
  // encryptSharedItem.
  const now = new Date().toISOString();
  const sharedPlaintext: VaultItem = {
    ...plainItem,
    id: opts?.itemId ?? uuidv4(),
    orgId,
    createdAt: now,
    updatedAt: now,
  };
  const encrypted = await encryptSharedItem(sharedPlaintext, familyPublicKey);
  return { ...encrypted, orgId, ...(plainItem.type ? { type: plainItem.type } : {}) };
}

